import * as React from 'react'
import { createRef, useEffect, useMemo } from 'react'

import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import {
  locationToBillToLocationDTOAddOpp,
  locationToServiceLocationDTOAddOpp,
} from '@user/store/entityLocations'
import { ENTITY_TYPES } from '@sales-center/common'
import { IAddOpportunityFormModel } from '../typings'
import { initialValues, useValidate } from '../formik'
import { useCreateOppActions } from '@user/components/Location'
import { useData, useGetOpportunityTitle, useOnSubmit } from '../hooks'
import { AddOpportunityForm } from '../AddOpportunityForm/AddOpportunityForm'
import { SelectLocations } from '@user/components/Location/SelectLocations/SelectLocations'

import { useAppDispatch } from '@sales-center/common'
import { useSelectCustomFieldData } from '@user/store/customFields'
import { customValuesToFormModel } from '@user/components/CustomFields'
import { useGetSalesFunnelData } from '@user/components/Settings/SalesFunnels/hooks'
import {
  selectAddOpportunitySlice,
  setAddOppBillTo,
  setAddOppLocations,
} from '@user/store/opportunity/addOpportunity/addOpportunitySlice'
import { useNoteCodes } from '@user/components/Opportunities/AddOpportunityPage/AddOpportunityForm/hooks/useNoteCodes'

export function AddNewOpportunity() {
  const createOppActions = useCreateOppActions()
  const dispatch = useAppDispatch()
  const dataLoading = useData()

  const { state } = createOppActions
  const { billTo: billToDTO, serviceLocations: serviceLocationsDTO } =
    useSelector(selectAddOpportunitySlice)
  useEffect(() => {
    if (state?.appliedSelections?.billTo) {
      dispatch(
        setAddOppBillTo(
          locationToBillToLocationDTOAddOpp(state?.appliedSelections?.billTo),
        ),
      )
    }
    if (state.appliedSelections?.serviceLocations) {
      dispatch(
        setAddOppLocations(
          state.appliedSelections?.serviceLocations.map((s) =>
            locationToServiceLocationDTOAddOpp(s),
          ),
        ),
      )
    }
  }, [
    state?.appliedSelections?.billTo,
    dispatch,
    state.appliedSelections?.serviceLocations,
  ])

  const { defaultFunnel, firstStage } = useGetSalesFunnelData(
    ENTITY_TYPES.Opportunity,
  )
  const externalSvcLocId = state.appliedSelections?.serviceLocations[0].id ?? ''

  const { customFields } = useSelectCustomFieldData()

  const { noteCodes } = useNoteCodes()

  const hydratedInitialValues = useMemo<IAddOpportunityFormModel>(
    () => ({
      ...initialValues,
      billToExternalLinkId: billToDTO?.externalLinkId || '',
      serviceLocations: serviceLocationsDTO,
      customFields: customValuesToFormModel(customFields),
      salesFunnelId: defaultFunnel?.id || '',
      opportunityStageId: firstStage?.id.toString() || '',

      externalLocationId: externalSvcLocId,
      addNoteToPestPac: Boolean(externalSvcLocId),
      serviceLocation:
        serviceLocationsDTO.length === 1
          ? serviceLocationsDTO[0].id.toString()
          : '',
      noteCode: Boolean(externalSvcLocId)
        ? noteCodes.find((code) => code.isDefault)?.name
        : '',
    }),
    [
      billToDTO?.externalLinkId,
      serviceLocationsDTO,
      externalSvcLocId,
      customFields,
      defaultFunnel?.id,
      firstStage?.id,
      noteCodes,
    ],
  )

  const title = useGetOpportunityTitle(state.appliedSelections?.billTo)

  const showForm =
    !!state.appliedSelections &&
    billToDTO &&
    serviceLocationsDTO &&
    !dataLoading

  const ref = createRef<HTMLDivElement>()

  const { validate } = useValidate()
  const onSubmit = useOnSubmit({
    entityType: ENTITY_TYPES.Opportunity,
  })

  return showForm ? (
    <Formik
      onSubmit={onSubmit}
      validate={validate}
      initialValues={hydratedInitialValues}
    >
      <AddOpportunityForm
        billTo={billToDTO}
        externalSvcLocId={externalSvcLocId}
        serviceLocations={serviceLocationsDTO}
        serviceLocationsLoading={false}
        innerRef={ref}
        title={title}
        entityType={ENTITY_TYPES.Opportunity}
        entityId={-1}
        createNewOpp
        noteCodes={noteCodes}
      />
    </Formik>
  ) : (
    <SelectLocations {...createOppActions} fullScreen={false} />
  )
}
